import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, KeyRound, Lock, Mail, ShieldCheck, TriangleAlert } from "lucide-react";
import { loginSchema, type LoginInput } from "@theadam/shared";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/misc";
import { ApiError, api } from "@/lib/api";
import { ME_KEY, clearSessionExpired } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { AuthLayout } from "./AuthLayout";

/** Yalnız e-posta hatırlanır (şifre asla tarayıcıya yazılmaz). */
const REMEMBER_KEY = "ta-remember-email";
function rememberedEmail(): string {
  try {
    return localStorage.getItem(REMEMBER_KEY) ?? "";
  } catch {
    return "";
  }
}

const inputCls =
  "h-12 w-full rounded-xl border border-input bg-background/60 pr-4 pl-11 text-[0.95rem] outline-none transition-[border,box-shadow] placeholder:text-muted-foreground/70 focus:border-accent focus:bg-card focus:ring-4 focus:ring-accent/15 aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/10";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const expired = Boolean((location.state as { expired?: boolean } | null)?.expired);
  const invited = Boolean((location.state as { invited?: boolean } | null)?.invited);
  const qc = useQueryClient();
  const saved = rememberedEmail();
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<LoginInput>({ resolver: zodResolver(loginSchema), defaultValues: { email: saved, password: "", remember: Boolean(saved) } });
  const { errors, isSubmitting } = form.formState;
  const remember = Boolean(form.watch("remember"));
  // İki aşamalı doğrulama: şifre doğruysa sunucu oturum yerine ikinci adım belirteci verir.
  const [challenge, setChallenge] = useState<{ token: string; email: string; remember: boolean } | null>(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [codeBusy, setCodeBusy] = useState(false);

  const finish = async (email: string, rememberMe: boolean) => {
    try {
      if (rememberMe) localStorage.setItem(REMEMBER_KEY, email);
      else localStorage.removeItem(REMEMBER_KEY);
    } catch {
      /* depolama kapalı olabilir */
    }
    clearSessionExpired();
    await qc.invalidateQueries({ queryKey: ME_KEY });
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from && from !== "/giris" ? from : "/", { replace: true });
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    setCodeBusy(true);
    setError(null);
    try {
      await api.post("/auth/login/2fa", { challenge: challenge.token, code });
      await finish(challenge.email, challenge.remember);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Doğrulanamadı");
      setCode("");
      if (err instanceof ApiError && (err.code === "challenge_expired" || err.code === "challenge_invalid")) setChallenge(null);
    } finally {
      setCodeBusy(false);
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const r = await api.post<{ ok?: boolean; twoFactorRequired?: boolean; challenge?: string }>("/auth/login", values);
      if (r.twoFactorRequired && r.challenge) {
        setChallenge({ token: r.challenge, email: values.email, remember: Boolean(values.remember) });
        setCode("");
        return;
      }
      await finish(values.email, Boolean(values.remember));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Giriş yapılamadı. Lütfen tekrar deneyin.");
      form.setValue("password", "");
      form.setFocus("password");
    }
  });

  const onPasswordKey = (e: React.KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState?.("CapsLock") ?? false);
  const emailReg = form.register("email");
  const passwordReg = form.register("password");

  if (challenge)
    return (
      <AuthLayout title="Doğrulama kodu" subtitle={useRecovery ? "Kurtarma kodlarınızdan birini girin." : "Doğrulama uygulamanızdaki 6 haneli kodu girin."}>
        <form onSubmit={submitCode} noValidate className="space-y-5">
          {error && <Alert tone="danger" title={error} />}
          <div className="relative">
            <KeyRound className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              autoFocus
              value={code}
              onChange={(e) => setCode(useRecovery ? e.target.value.toUpperCase().slice(0, 12) : e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode={useRecovery ? "text" : "numeric"}
              autoComplete="one-time-code"
              aria-label={useRecovery ? "Kurtarma kodu" : "Doğrulama kodu"}
              placeholder={useRecovery ? "ABCDE-12345" : "123456"}
              className={cn(inputCls, "font-mono text-lg tracking-[0.35em]")}
            />
          </div>
          <Button type="submit" className="h-12 w-full rounded-xl text-[0.95rem]" size="lg" loading={codeBusy} disabled={useRecovery ? code.replace(/[^A-F0-9]/g, "").length < 10 : code.length !== 6}>
            Doğrula ve giriş yap <ArrowRight />
          </Button>
          <div className="flex items-center justify-between text-sm">
            <button type="button" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" onClick={() => (setChallenge(null), setError(null))}>
              <ArrowLeft className="size-4" /> Geri
            </button>
            <button type="button" className="font-medium text-accent underline-offset-4 hover:underline" onClick={() => (setUseRecovery((v) => !v), setCode(""))}>
              {useRecovery ? "Uygulama kodu kullan" : "Telefonum yanımda değil"}
            </button>
          </div>
        </form>
      </AuthLayout>
    );

  return (
    <AuthLayout title={saved ? "Tekrar hoş geldiniz" : "Hoş geldiniz"} subtitle="Devam etmek için hesabınızla oturum açın.">
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        {invited && !error && <Alert tone="success" title="Davet kabul edildi">E-posta adresiniz ve şifrenizle giriş yapın.</Alert>}
        {expired && !error && <Alert tone="info" title="Oturumunuz sona erdi">Tekrar giriş yapınca kaldığınız sayfaya dönersiniz.</Alert>}
        {error && <Alert tone="danger" title={error} />}

        <div className="space-y-1.5">
          <label htmlFor="login-email" className="text-sm font-medium">
            E-posta
          </label>
          <div className="relative">
            <Mail className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              id="login-email"
              type="email"
              autoComplete="username"
              inputMode="email"
              autoFocus={!saved}
              placeholder="ornek@firma.com"
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? "login-email-error" : undefined}
              className={inputCls}
              {...emailReg}
            />
          </div>
          {errors.email && (
            <p id="login-email-error" className="text-xs text-danger">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor="login-password" className="text-sm font-medium">
              Şifre
            </label>
            <Link to="/sifremi-unuttum" className="text-sm font-medium text-accent underline-offset-4 hover:underline">
              Şifremi unuttum
            </Link>
          </div>
          <div className="relative">
            <Lock className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              autoFocus={Boolean(saved)}
              placeholder="Şifreniz"
              aria-invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? "login-password-error" : capsLock ? "login-caps" : undefined}
              className={cn(inputCls, "pr-12")}
              onKeyUp={onPasswordKey}
              onKeyDown={onPasswordKey}
              {...passwordReg}
              onBlur={(e) => {
                setCapsLock(false);
                void passwordReg.onBlur(e);
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
            </button>
          </div>
          {errors.password && (
            <p id="login-password-error" className="text-xs text-danger">
              {errors.password.message}
            </p>
          )}
          {capsLock && (
            <p id="login-caps" className="flex items-center gap-1.5 text-xs font-medium text-warning">
              <TriangleAlert className="size-3.5" aria-hidden /> Caps Lock açık
            </p>
          )}
        </div>

        <label className="group flex cursor-pointer items-start gap-3 rounded-xl border border-transparent p-1 select-none">
          <input type="checkbox" className="peer sr-only" {...form.register("remember")} />
          <span
            aria-hidden
            className={cn(
              "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors peer-focus-visible:ring-4 peer-focus-visible:ring-accent/25",
              remember ? "border-accent bg-accent text-accent-foreground" : "border-input bg-card group-hover:border-accent/60",
            )}
          >
            {remember && <Check className="size-3.5" strokeWidth={3} />}
          </span>
          <span className="leading-tight">
            <span className="block text-sm font-medium">Beni hatırla</span>
            <span className="block text-xs text-muted-foreground">30 gün oturum açık kalır. Ortak bilgisayarlarda işaretlemeyin.</span>
          </span>
        </label>

        <Button type="submit" className="h-12 w-full rounded-xl text-[0.95rem] shadow-lg shadow-primary/20" size="lg" loading={isSubmitting}>
          {isSubmitting ? "Giriş yapılıyor…" : "Giriş yap"} {!isSubmitting && <ArrowRight />}
        </Button>

        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="size-3.5 text-success" aria-hidden /> Güvenli bağlantı · şifreniz şifrelenerek iletilir
        </p>
      </form>
    </AuthLayout>
  );
}
